import { useState, useEffect } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemText, Accordion, AccordionSummary, AccordionDetails, Alert, Button } from '@mui/material';
import { Menu as MenuIcon, ExpandMore, Source } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import HotTopicModal from './HotTopicModal';
import { generateSignedUrl } from '../services/apiClient';
import DocumentViewerModal from './DocumentViewerModal';
import FaqModal from './FaqModal';


export default function LeftNav({ sources, allHotTopics, hotTopicsError, allCalendars, calendarsError, selectedClientCode, allFaqs, faqsError, onAskFaq}) {
    const [filteredHotTopics, setFilteredHotTopics] = useState([]);
    const [isHotTopicModalOpen, setIsHotTopicModalOpen] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [filteredCalendars, setFilteredCalendars] = useState([]);
    const [urlCache, setUrlCache] = useState({});

    const [filteredFaqs, setFilteredFaqs] = useState([]);
    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
    const [selectedFaq, setSelectedFaq] = useState(null);
    const [showFaqs, setShowFaqs] = useState(true);


    // Collapsible controls
    const [showHotTopics, setShowHotTopics] = useState(true);
    const [showCalendars, setShowCalendars] = useState(true);
    const [showSources, setShowSources] = useState(true);

    // State for Document Viewer
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [docContent, setDocContent] = useState('');
    const [docContentType, setDocContentType] = useState('');
    const [selectedFile, setSelectedFile] = useState('');
    const [isDocLoading, setIsDocLoading] = useState(false);

    useEffect(() => {
        if (selectedClientCode) {
            if (allHotTopics?.length > 0) {
                setFilteredHotTopics(allHotTopics.filter(topic => topic.client_code === selectedClientCode));
            } else {
                setFilteredHotTopics([]);
            }
            if (allCalendars?.length > 0) {
                setFilteredCalendars(allCalendars.filter(cal => cal.client_code === selectedClientCode));
            } else {
                setFilteredCalendars([]);
            }
            if (allFaqs?.length > 0) {
                setFilteredFaqs(allFaqs.filter(faq => faq.client_code === selectedClientCode));
            } else {
                setFilteredFaqs([]);
            }
        } else {
            setFilteredHotTopics([]);
            setFilteredCalendars([]);
            setFilteredFaqs([]);
        }
    }, [selectedClientCode, allHotTopics, allCalendars, allFaqs]);

    const handleTopicClick = (topic) => {
        setSelectedTopic(topic);
        setIsHotTopicModalOpen(true);
    };

    const handleCloseHotTopicModal = () => {
        setIsHotTopicModalOpen(false);
        setSelectedTopic(null);
    };

    const handleFaqClick = (faq) => {
        setSelectedFaq(faq);
        setIsFaqModalOpen(true);
    };

    const handleCloseFaqModal = () => {
        setIsFaqModalOpen(false);
        setSelectedFaq(null);
    };


    const openDocumentInModal = async (file, sourceType) => {
        setIsDocLoading(true);
        setIsDocModalOpen(true);
        const fileName = file.filename || file.file_name;
        setSelectedFile(fileName);
        
        const fileExtension = fileName.split('.').pop().toLowerCase();

        try {
            let signedUrl = '';
            const cacheEntry = urlCache[fileName];

            if (cacheEntry && Date.now() < cacheEntry.expires) {
                signedUrl = cacheEntry.url;
            } else {
                const response = await generateSignedUrl(fileName, sourceType);
                signedUrl = response.signed_url;
                if (!signedUrl) throw new Error('Could not retrieve a valid URL.');
                setUrlCache(prev => ({
                    ...prev,
                    [fileName]: { url: signedUrl, expires: Date.now() + 15 * 60 * 1000 }
                }));
            }
            
            const fileResponse = await fetch(signedUrl);
            if (!fileResponse.ok) throw new Error(`Failed to download file: ${fileResponse.statusText}`);

            if (fileExtension === 'pdf') {
                const pdfBlob = await fileResponse.blob();
                setDocContent(URL.createObjectURL(pdfBlob));
                setDocContentType('pdf');
            } else if (fileExtension === 'html') {
                setDocContent(await fileResponse.text());
                setDocContentType('html');
            } else {
                setDocContent(await fileResponse.text());
                setDocContentType('text');
            }
        } catch (error) {
            console.error(`Failed to get/download file for ${sourceType}:`, error);
            alert(`Error: ${error.message}`);
            setIsDocModalOpen(false);
        } finally {
            setIsDocLoading(false);
        }
    };
    
    const handleCloseDocModal = () => {
        setIsDocModalOpen(false);
        setDocContent('');
        setSelectedFile('');
        setDocContentType('');
    };
    
    const renderHotTopicsContent = () => {
        if (hotTopicsError) {
            return <Alert severity="error" sx={{ m: 1 }}>{hotTopicsError}</Alert>;
        }

        if (!selectedClientCode) {
            return (
                <Typography 
                    variant="caption" 
                    sx={{ p: 2, textAlign: 'center', display: 'block' }}
                >
                    Select a client to see hot topics.
                </Typography>
            );
        }

        if (filteredHotTopics.length === 0) {
            return (
                <Typography 
                    variant="caption" 
                    sx={{ p: 2, textAlign: 'center', display: 'block' }}
                >
                    No hot topics for this client.
                </Typography>
            );
        }

        return (
            <List dense disablePadding>
                {filteredHotTopics.map((item) => {
                    const firstLine = item.content ? item.content.split('\n')[0] : '';
                    return (
                        <ListItem
                            key={item.hot_topic_id}
                            button
                            onClick={() => handleTopicClick(item)}
                            sx={{
                                borderBottom: '1px solid #eee',
                                '&:last-child': { borderBottom: 0 }
                            }}
                        >
                            <ListItemText
                                primary={
                                    <ReactMarkdown
                                        linkTarget="_blank"
                                        components={{
                                            p: ({ children }) => (
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {children}
                                                </Typography>
                                            )
                                        }}
                                    >
                                        {firstLine}
                                    </ReactMarkdown>
                                }
                            />

                        </ListItem>
                    );
                })}
            </List>
        );
    };


    const renderCalendarsContent = () => {
        if (calendarsError) return <Alert severity="error" sx={{ m: 1 }}>{calendarsError}</Alert>;
        if (!selectedClientCode) return <Typography variant="caption" sx={{ p: 2, textAlign: 'center', display: 'block' }}>Select a client to see calendars.</Typography>;
        if (filteredCalendars.length === 0) return <Typography variant="caption" sx={{ p: 2, textAlign: 'center', display: 'block' }}>No calendars for this client.</Typography>;
        return (
            <List dense disablePadding>
                {filteredCalendars.map((item) => (
                    <ListItem key={item.calendar_id} button onClick={() => openDocumentInModal(item, 'calendars')} sx={{ borderBottom: '1px solid #eee', '&:last-child': { borderBottom: 0 } }}>
                        <ListItemText primary={item.file_name} />
                    </ListItem>
                ))}
            </List>
        );
    };

    const renderFaqsContent = () => {
        if (faqsError)
            return (
                <Alert severity="error" sx={{ m: 1 }}>
                    {faqsError}
                </Alert>
            );
        if (!selectedClientCode)
            return (
                <Typography
                    variant="caption"
                    sx={{ p: 2, textAlign: 'center', display: 'block' }}
                >
                    Select a client to see FAQs.
                </Typography>
            );
        if (filteredFaqs.length === 0)
            return (
                <Typography
                    variant="caption"
                    sx={{ p: 2, textAlign: 'center', display: 'block' }}
                >
                    No FAQs for this client.
                </Typography>
            );

        return (
            <List dense disablePadding>
                {filteredFaqs.map((item) => (
                    <ListItem
                        key={item.faq_id}
                        button
                        onClick={() => handleFaqClick(item)}
                        sx={{
                            borderBottom: '1px solid #eee',
                            '&:last-child': { borderBottom: 0 }
                        }}
                    >
                        <ListItemText
                            primary={item.content}
                            primaryTypographyProps={{
                                sx: {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                }
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        );
    };


    return (
        <Box>
            {/* Hot Topics Section */}
            <Box sx={{ mb: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: '#4a0072',
                        color: 'white',
                        p: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: 1
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Hot Topics</Typography>
                    <MenuIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setShowHotTopics(prev => !prev)}
                    />
                </Paper>

                {showHotTopics && (
                    <Box sx={{ border: '1px solid #eee', borderTop: 0, bgcolor: 'white' }}>
                        {renderHotTopicsContent()}
                    </Box>
                )}
            </Box>
            
            {/* Calendars Section */}
            <Box sx={{ mb: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: '#4a0072',
                        color: 'white',
                        p: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: 1
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Calendars</Typography>
                    <MenuIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setShowCalendars(prev => !prev)}
                    />
                </Paper>
                {showCalendars && (
                    <Box sx={{ border: '1px solid #eee', borderTop: 0, bgcolor: 'white' }}>
                        {renderCalendarsContent()}
                    </Box>
                )}
            </Box>
            
            {/* Sources Section */}
            <Box sx={{ mb: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: '#4a0072',
                        color: 'white',
                        p: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: 1
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Sources</Typography>
                    <MenuIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setShowSources(prev => !prev)}
                    />
                </Paper>
                {showSources && (
                    <Box sx={{ border: '1px solid #eee', borderTop: 0, bgcolor: 'white' }}>
                        {sources && sources.length > 0 ? (
                            sources.map((source, index) => (
                                <Accordion key={index} elevation={0} square sx={{ '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Source sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                                            {source.filename || `Source ${index + 1}`}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Typography variant="caption">{source.chunk_content}</Typography>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={() => openDocumentInModal(source, source.source_type)}
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            View Document
                                        </Button>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <Typography variant="caption" sx={{ p: 2, textAlign: 'center', display: 'block' }}>No sources for this query.</Typography>
                        )}
                    </Box>
                )}
            </Box>

            {/* FAQs Section */}
            <Box sx={{ mb: 2 }}>
                <Paper elevation={0} sx={{ backgroundColor: '#4a0072', color: 'white', p: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>FAQs</Typography>
                    <MenuIcon sx={{ cursor: 'pointer' }} onClick={() => setShowFaqs(prev => !prev)} />
                </Paper>
                {showFaqs && (
                    <Box sx={{ border: '1px solid #eee', borderTop: 0, bgcolor: 'white' }}>
                        {renderFaqsContent()}
                    </Box>
                )}
            </Box>

            <FaqModal 
                open={isFaqModalOpen} 
                onClose={handleCloseFaqModal} 
                faq={selectedFaq} 
                onAsk={() => {
                    onAskFaq(selectedFaq.content);
                    handleCloseFaqModal();
                }}
            />


            <Paper variant="outlined" sx={{ p: 2, height: 100 }}>
                <Typography>Other Widgets</Typography>
            </Paper>
            
            <HotTopicModal open={isHotTopicModalOpen} onClose={handleCloseHotTopicModal} hotTopic={selectedTopic} />
            <DocumentViewerModal 
                open={isDocModalOpen} 
                onClose={handleCloseDocModal} 
                content={docContent}
                contentType={docContentType}
                fileName={selectedFile} 
                loading={isDocLoading} 
            />
        </Box>
    );
}
